import { RefObject, useEffect, useLayoutEffect, useRef, useState } from 'react';

// Breakpoints defined here: https://ionicframework.com/docs/layout/css-utilities#ionic-breakpoints
const breakpoints = {
  xs: 0,
  sm: 576,
  md: 768,
  lg: 992,
  xl: 1200,
};

interface BreakpointState {
  isXs: boolean;
  isSm: boolean;
  isMd: boolean;
  isLg: boolean;
  isXl: boolean;
  activeBreakpoint: 'xs' | 'sm' | 'md' | 'lg' | 'xl';
}

function useIonicBreakpoints(): BreakpointState {
  const getInitialState = (): BreakpointState => {
    if (typeof window === 'undefined') {
      return {
        isXs: true,
        isSm: false,
        isMd: false,
        isLg: false,
        isXl: false,
        activeBreakpoint: 'xs',
      };
    }
    const width = window.innerWidth;
    return getBreakPointStateFromWidth(width);
  };

  const [breakpointState, setBreakpointState] =
    useState<BreakpointState>(getInitialState);

  useEffect(() => {
    if (typeof window === 'undefined') {
      return;
    }

    const mediaQueryLists = {
      sm: window.matchMedia(`(min-width: ${breakpoints.sm}px)`),
      md: window.matchMedia(`(min-width: ${breakpoints.md}px)`),
      lg: window.matchMedia(`(min-width: ${breakpoints.lg}px)`),
      xl: window.matchMedia(`(min-width: ${breakpoints.xl}px)`),
    };

    const handleResize = () => {
      setBreakpointState(getInitialState());
    };

    Object.values(mediaQueryLists).forEach((mql) => {
      mql.addEventListener('change', handleResize);
    });

    // Initial check in case the state calculated on mount differs slightly
    handleResize();

    // Cleanup function: remove listeners
    return () => {
      Object.values(mediaQueryLists).forEach((mql) => {
        mql.removeEventListener('change', handleResize);
      });
    };
  }, []);

  return breakpointState;
}

function useIonicBreakpointsForElementRef(
  ref: RefObject<HTMLElement>
): BreakpointState {
  const currentWidth = useRef(ref.current?.offsetWidth ?? 0);

  const [breakpointState, setBreakpointState] = useState<BreakpointState>(
    getBreakPointStateFromWidth(currentWidth.current)
  );

  useLayoutEffect(() => {
    const element = ref.current;
    if (!element) {
      return;
    }

    const observer = new ResizeObserver((entries) => {
      const entry = entries[0];

      const width = entry.contentRect?.width ?? element?.offsetWidth ?? 0;
      if (width === currentWidth.current) {
        console.info('Width has not changed, skipping state update');
        return;
      }

      setBreakpointState(getBreakPointStateFromWidth(width));
      currentWidth.current = width;
    });

    observer.observe(element);

    return () => {
      observer.disconnect();
    };
  }, [ref]);

  return breakpointState;
}

function getBreakPointStateFromWidth(width: number): BreakpointState {
  const isXl = width >= breakpoints.xl;
  const isLg = width >= breakpoints.lg;
  const isMd = width >= breakpoints.md;
  const isSm = width >= breakpoints.sm;

  let activeBreakpoint: BreakpointState['activeBreakpoint'] = 'xs';
  if (isXl) {
    activeBreakpoint = 'xl';
  } else if (isLg) {
    activeBreakpoint = 'lg';
  } else if (isMd) {
    activeBreakpoint = 'md';
  } else if (isSm) {
    activeBreakpoint = 'sm';
  }

  return { isXs: true, isSm, isMd, isLg, isXl, activeBreakpoint };
}

export { useIonicBreakpoints, useIonicBreakpointsForElementRef };
